// Land Transfer Tax Calculator for Canadian provinces

export interface TaxBracket {
  limit: number;
  rate: number;
}

export interface LTTResult {
  provincialTax: number;
  municipalTax: number;
  totalTax: number;
  provincialRebate: number;
  municipalRebate: number;
  netTax: number;
}

// Ontario Land Transfer Tax Brackets
const ontarioLTTBrackets: TaxBracket[] = [
  { limit: 55000, rate: 0.005 }, // 0.5% on first $55,000
  { limit: 250000, rate: 0.01 }, // 1.0% on $55,000 - $250,000
  { limit: 400000, rate: 0.015 }, // 1.5% on $250,000 - $400,000
  { limit: 2000000, rate: 0.02 }, // 2.0% on $400,000 - $2,000,000
  { limit: Infinity, rate: 0.025 }, // 2.5% on $2,000,000+
];

// Toronto uses same brackets (applied in addition to Ontario)
const torontoLTTBrackets: TaxBracket[] = ontarioLTTBrackets;

// BC Land Transfer Tax Brackets
const bcLTTBrackets: TaxBracket[] = [
  { limit: 200000, rate: 0.01 }, // 1% on first $200,000
  { limit: 2000000, rate: 0.02 }, // 2% on $200,000 - $2,000,000
  { limit: 3000000, rate: 0.03 }, // 3% on $2,000,000 - $3,000,000
  { limit: Infinity, rate: 0.05 }, // 5% on $3,000,000+
];

// Quebec (Montreal) - Welcome Tax
const quebecLTTBrackets: TaxBracket[] = [
  { limit: 57200, rate: 0.005 }, // 0.5% on first $57,200
  { limit: 286900, rate: 0.01 }, // 1.0% on $57,200 - $286,900
  { limit: Infinity, rate: 0.015 }, // 1.5% on $286,900+
];

/**
 * Calculate tax based on progressive bracket system
 */
const calculateProgressiveTax = (homePrice: number, brackets: TaxBracket[]): number => {
  let tax = 0;
  let previousLimit = 0;

  for (const bracket of brackets) {
    if (homePrice <= previousLimit) break;

    const taxableAmount = Math.min(homePrice, bracket.limit) - previousLimit;
    tax += taxableAmount * bracket.rate;
    previousLimit = bracket.limit;

    if (homePrice <= bracket.limit) break;
  }

  return Math.round(tax * 100) / 100;
};

/**
 * Calculate Land Transfer Tax for Ontario
 */
export const calculateOntarioLTT = (
  homePrice: number,
  isFirstTimeBuyer: boolean,
  isToronto: boolean
): LTTResult => {
  // Calculate provincial tax
  const provincialTax = calculateProgressiveTax(homePrice, ontarioLTTBrackets);

  // Calculate municipal tax (Toronto only)
  const municipalTax = isToronto ? calculateProgressiveTax(homePrice, torontoLTTBrackets) : 0;

  // Calculate rebates for first-time buyers
  const provincialRebate = isFirstTimeBuyer ? Math.min(4000, provincialTax) : 0;
  const municipalRebate = isFirstTimeBuyer && isToronto ? Math.min(4475, municipalTax) : 0;

  const totalTax = provincialTax + municipalTax;
  const netTax = totalTax - provincialRebate - municipalRebate;

  return {
    provincialTax,
    municipalTax,
    totalTax,
    provincialRebate,
    municipalRebate,
    netTax,
  };
};

/**
 * Calculate Land Transfer Tax for BC
 */
export const calculateBCLTT = (
  homePrice: number,
  isFirstTimeBuyer: boolean
): LTTResult => {
  const provincialTax = calculateProgressiveTax(homePrice, bcLTTBrackets);

  // BC First Time Home Buyers' Program exemption (up to $500,000)
  // Full exemption on first $500k, partial on $500k-$525k
  let provincialRebate = 0;
  if (isFirstTimeBuyer) {
    if (homePrice <= 500000) {
      provincialRebate = provincialTax;
    } else if (homePrice <= 525000) {
      // Partial exemption
      const fullExemption = calculateProgressiveTax(500000, bcLTTBrackets);
      provincialRebate = fullExemption;
    }
  }

  const netTax = provincialTax - provincialRebate;

  return {
    provincialTax,
    municipalTax: 0,
    totalTax: provincialTax,
    provincialRebate,
    municipalRebate: 0,
    netTax,
  };
};

/**
 * Calculate Land Transfer Tax for Quebec
 */
export const calculateQuebecLTT = (
  homePrice: number,
  _isFirstTimeBuyer: boolean
): LTTResult => {
  const provincialTax = calculateProgressiveTax(homePrice, quebecLTTBrackets);

  // Quebec doesn't have a provincial first-time buyer rebate for welcome tax
  // but some municipalities may offer rebates
  const provincialRebate = 0;

  const netTax = provincialTax - provincialRebate;

  return {
    provincialTax,
    municipalTax: 0,
    totalTax: provincialTax,
    provincialRebate,
    municipalRebate: 0,
    netTax,
  };
};

/**
 * Main function to calculate LTT based on location
 */
export const calculateLandTransferTax = (
  homePrice: number,
  location: string,
  isFirstTimeBuyer: boolean
): LTTResult => {
  switch (location) {
    case 'toronto':
      return calculateOntarioLTT(homePrice, isFirstTimeBuyer, true);
    case 'ontario':
      return calculateOntarioLTT(homePrice, isFirstTimeBuyer, false);
    case 'vancouver':
    case 'bc':
      return calculateBCLTT(homePrice, isFirstTimeBuyer);
    case 'montreal':
    case 'quebec':
      return calculateQuebecLTT(homePrice, isFirstTimeBuyer);
    default:
      return calculateOntarioLTT(homePrice, isFirstTimeBuyer, false);
  }
};

/**
 * Generate AI insights for land transfer tax
 */
export const getLTTInsights = (
  homePrice: number,
  result: LTTResult,
  isFirstTimeBuyer: boolean
): string[] => {
  const insights: string[] = [];

  // Total closing costs estimate
  const estimatedClosingCosts = result.netTax + (homePrice * 0.015); // LTT + other costs (~1.5%)
  insights.push(
    `💵 **Total Closing Costs:** Beyond the land transfer tax of $${result.netTax.toLocaleString()}, expect approximately $${estimatedClosingCosts.toLocaleString()} in total closing costs (including legal fees, title insurance, and adjustments).`
  );

  // First-time buyer insight
  if (!isFirstTimeBuyer && result.provincialRebate === 0) {
    const potentialSavings = calculateOntarioLTT(homePrice, true, result.municipalTax > 0);
    const savings = potentialSavings.provincialRebate + potentialSavings.municipalRebate;
    if (savings > 0) {
      insights.push(
        `🏠 **First-Time Buyer Benefit:** If this were your first home purchase, you could save up to $${savings.toLocaleString()} in land transfer tax rebates.`
      );
    }
  }

  // Budget planning
  const savingsNeeded = result.netTax + (homePrice * 0.2); // LTT + 20% down payment
  insights.push(
    `📊 **Budget Planning:** For a $${homePrice.toLocaleString()} home, budget for $${savingsNeeded.toLocaleString()} in total upfront costs (20% down payment + land transfer tax + other closing costs).`
  );

  // Rebate maximization
  if (isFirstTimeBuyer && (result.provincialRebate > 0 || result.municipalRebate > 0)) {
    const totalRebate = result.provincialRebate + result.municipalRebate;
    insights.push(
      `✨ **Rebate Applied:** Congratulations! As a first-time homebuyer, you're saving $${totalRebate.toLocaleString()} through rebate programs.`
    );
  }

  return insights;
};
